import { Dispatch, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export interface UnitizationRow {
  trafficType: string;
  hectareCoefficient: string;
  trafficWeightCoefficient: string;
  weightCoefficient: string;
  distance: string;
  comments: string;
  weightValue: number;
  kayttomaksuAmount: string;
  muuTripCount: string;
  muuVehicleWeight: string;
  muuPayload: string;
}

export interface Property {
  id?: number;
  name: string;
  code: string;
  address: string;
  user: any;
  propertyGeometries: any;
  units: number;
  unitizationRows?: UnitizationRow[];
}

export interface PropertyState {
  property: Property | undefined;
  properties: Partial<Property>[];
  open: boolean;
  allCodes: [];
  editable: boolean;
  status: "idle" | "loading" | "failed";
}

const initialState: PropertyState = {
  property: undefined,
  properties: [],
  open: false,
  allCodes: [],
  editable: false,
  status: "idle",
};

export const fetchProperties = createAsyncThunk(
  "properties/fetchProperties",
  async (associationId: number) => {
    const response = await axiosInstance(
      `/properties?associationId=${associationId}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchPropertyByCode = createAsyncThunk(
  "properties/fetchPropertyByCode",
  async (code: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/properties/property?code=${code}`
      );
      const features = response.data?.features;

      if (!features || !features.length) {
        return rejectWithValue("Kiinteistöä ei löytynyt");
      }

      return features[0];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Kiinteistön haku ei onnistunut"
      );
    }
  }
);

export const fetchPropertiesForMap = createAsyncThunk(
  "properties/fetchPropertiesForMap",
  async (associationId: number) => {
    const response = await axiosInstance(
      `/properties/map?associationId=${associationId}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchAllPropertyCodes = createAsyncThunk(
  "properties/fetchAllPropertyCodes",
  async () => {
    const response = await axiosInstance("/properties/codes");
    return response.data;
  }
);

const propertySlice = createSlice({
  name: "property",
  initialState,
  reducers: {
    setProperty(state, action) {
      state.property = action.payload;
    },
    addProperty(state, action) {
      state.properties.push(action.payload);
    },
    updateProperty(state, action) {
      const propertyId = state.properties.findIndex(
        (r) => r.id === action.payload.id
      );
      state.properties[propertyId] = action.payload;
    },
    deleteProperty(state, action) {
      state.properties = state.properties.filter(
        (r: any) => r.id !== action.payload
      );
    },
    setOpen(state, action) {
      state.open = action.payload;
    },
    setEditable(state) {
      state.editable = !state.editable;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchProperties.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchProperties.fulfilled, (state, action) => {
      state.properties = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchProperties.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchPropertiesForMap.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchPropertiesForMap.fulfilled, (state, action) => {
      state.properties = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchPropertiesForMap.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchAllPropertyCodes.fulfilled, (state, action) => {
      state.allCodes = action.payload;
    });
  },
});

export function setProperty(property: Property) {
  return async (dispatch: Dispatch) => {
    dispatch(propertySlice.actions.setProperty(property));
  };
}

export function createProperty(property: Property) {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axiosInstance.post(`/properties`, property);
      dispatch(propertySlice.actions.addProperty(response.data));
    } catch (error: any) {
      throw error;
    }
  };
}

export function updateProperty(id: number, property: any) {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axiosInstance.put(`/properties/${id}`, property);
      dispatch(propertySlice.actions.updateProperty(response.data));
    } catch (error: any) {
      throw error;
    }
  };
}

export function deleteProperty(id: number) {
  return async (dispatch: Dispatch) => {
    try {
      await axiosInstance.delete(`/properties/${id}`);
      dispatch(propertySlice.actions.deleteProperty(id));
    } catch (error: any) {
      throw error;
    }
  };
}

export function editPropertyForm() {
  return async (dispatch: Dispatch) => {
    dispatch(propertySlice.actions.setEditable());
  };
}

export function openPropertyForm(open: boolean) {
  return async (dispatch: Dispatch) => {
    dispatch(propertySlice.actions.setOpen(open));
  };
}

export default propertySlice.reducer;
